// Which apps the signed-in person's plan includes, for the Install Desktop Apps page.
//
// Today the source is GET /api/entitlements on the Drive BFF (plan's EnabledApps intersected with
// the token's apps claim; see Fg.Entitlements). Every app's shell already registers the Drive BFF
// and token through useAppearanceSync, so nothing per app is needed.
//
// Seam for plans (FPT-9): when plans replace EnabledApps, call setEnabledAppsSource once at start
// with a function returning the plan's app keys. Old and new key names are both understood
// (normalizePlanKeys), and null means "could not tell", which offers every app (fail-open).

import { useEffect, useState } from "react";
import { suiteApiSession } from "./appearance";

export type EnabledAppsSource = () => Promise<readonly string[] | null>;

export const entitlementsEndpointSource: EnabledAppsSource = async () => {
    const session = suiteApiSession();
    if (!session) return null;
    try {
        const res = await fetch(`${session.driveBaseUrl.replace(/\/+$/, "")}/api/entitlements`, {
            headers: { Authorization: `Bearer ${session.token}` }
        });
        if (!res.ok) return null;
        const body = (await res.json()) as { apps?: unknown };
        return Array.isArray(body.apps) ? body.apps.filter((a): a is string => typeof a === "string") : null;
    } catch {
        return null;
    }
};

let source: EnabledAppsSource = entitlementsEndpointSource;

/** Replaces where the plan's apps come from (FPT-9 plans). */
export function setEnabledAppsSource(next: EnabledAppsSource): void {
    source = next;
}

export type EnabledAppsState = { status: "loading" } | { status: "ready"; apps: readonly string[] | null };

/** Loads the plan's app keys once per mount. `apps: null` = unknown, offer everything. */
export function useEnabledApps(): EnabledAppsState {
    const [state, setState] = useState<EnabledAppsState>({ status: "loading" });
    useEffect(() => {
        let live = true;
        source()
            .catch(() => null)
            .then((apps) => {
                if (live) setState({ status: "ready", apps });
            });
        return () => {
            live = false;
        };
    }, []);
    return state;
}
